/**
 * Balance Sheet — Assets / Liabilities / Equity as-at any date.
 *
 * All non-cash figures come from the SAME canonical endpoints the Audit Pack
 * uses, so a Balance Sheet number can never disagree with the rest of the
 * finance stack. Bank / cash / petty-cash / loans / opening-equity are
 * operator-maintained inputs stored on /balance-sheet/settings.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Scale, RefreshCw, Settings2, Printer, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) =>
  `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Row = ({ label, value, muted = false, bold = false, indent = false, testid }) => (
  <div className={`flex items-center justify-between py-1.5 ${indent ? "pl-4" : ""} ${bold ? "font-semibold" : ""} ${muted ? "text-slate-500" : ""}`}>
    <span className="text-sm">{label}</span>
    <span className="tabular-nums text-sm" data-testid={testid}>{fmt(value)}</span>
  </div>
);

const Section = ({ title, colorClass, children, total, totalLabel, totalTestid }) => (
  <Card>
    <CardHeader className={`${colorClass} text-white py-2.5`}>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent className="p-4 space-y-0.5">
      {children}
      <div className="border-t mt-2 pt-2 flex items-center justify-between font-bold">
        <span className="text-sm uppercase tracking-wide">{totalLabel}</span>
        <span className="tabular-nums" data-testid={totalTestid}>{fmt(total)}</span>
      </div>
    </CardContent>
  </Card>
);

export default function BalanceSheet() {
  const [asAt, setAsAt] = useState(todayISO());
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSheet = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/balance-sheet?as_at=${asAt}`);
      setSheet(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load balance sheet");
    } finally {
      setLoading(false);
    }
  }, [asAt]);

  const fetchSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/balance-sheet/settings`);
      setSettings(r.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchSheet(); }, [fetchSheet]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const openSettings = () => {
    if (!settings) fetchSettings();
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = {
        bank_balance: Number(settings.bank_balance) || 0,
        cash_on_hand: Number(settings.cash_on_hand) || 0,
        petty_cash_float: Number(settings.petty_cash_float) || 0,
        opening_equity: Number(settings.opening_equity) || 0,
        loans_payable: Number(settings.loans_payable) || 0,
        other_assets_label: settings.other_assets_label || "",
        other_assets_amount: Number(settings.other_assets_amount) || 0,
        other_liabilities_label: settings.other_liabilities_label || "",
        other_liabilities_amount: Number(settings.other_liabilities_amount) || 0,
        notes: settings.notes || "",
      };
      const r = await axios.put(`${API}/balance-sheet/settings`, payload);
      setSettings(r.data);
      toast.success("Balance sheet settings saved");
      setSettingsOpen(false);
      fetchSheet();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const printSheet = () => window.print();

  const balancedBadge = useMemo(() => {
    if (!sheet) return null;
    if (sheet.balanced) {
      return (
        <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-100 px-2 py-1 rounded text-xs font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5" /> Balanced
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-100 px-2 py-1 rounded text-xs font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" /> Out of balance
      </span>
    );
  }, [sheet]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="balance-sheet-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-slate-900 text-white rounded-lg p-2">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Balance Sheet</h1>
            <p className="text-sm text-slate-500">Assets · Liabilities · Equity</p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap no-print">
          <div>
            <Label className="text-xs">As at date</Label>
            <Input
              type="date"
              value={asAt}
              onChange={(e) => setAsAt(e.target.value)}
              className="w-40"
              data-testid="balance-sheet-as-at"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchSheet} disabled={loading} data-testid="balance-sheet-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={openSettings} data-testid="balance-sheet-edit-inputs">
            <Settings2 className="h-4 w-4 mr-1" /> Edit inputs
          </Button>
          <Button size="sm" onClick={printSheet} data-testid="balance-sheet-print">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      {!sheet ? (
        <p className="text-center py-10 text-slate-500">Loading balance sheet…</p>
      ) : (
        <>
          {/* Top summary bar */}
          <Card>
            <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs text-slate-500">As at</p>
                <p className="text-lg font-semibold" data-testid="balance-sheet-header-date">{sheet.as_at_date}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total Assets</p>
                <p className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="bs-total-assets-hero">
                  {fmt(sheet.totals.total_assets)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total Liabilities</p>
                <p className="text-xl font-bold text-red-700 tabular-nums">{fmt(sheet.totals.total_liabilities)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total Equity</p>
                <p className="text-xl font-bold text-indigo-700 tabular-nums" data-testid="bs-total-equity-hero">
                  {fmt(sheet.totals.total_equity)}
                </p>
              </div>
              <div className="text-right">{balancedBadge}</div>
            </CardContent>
          </Card>

          {/* 3-column: assets | liabilities+equity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section
              title="ASSETS"
              colorClass="bg-emerald-700"
              total={sheet.totals.total_assets}
              totalLabel="Total Assets"
              totalTestid="bs-total-assets"
            >
              <Row label="Bank balance" value={sheet.assets.bank_balance} testid="bs-bank" />
              <Row label="Cash on hand (till + safe)" value={sheet.assets.cash_on_hand} testid="bs-cash" />
              <Row label="Petty cash float" value={sheet.assets.petty_cash_float} testid="bs-petty" />
              <Row
                label={`Stock at cost (${sheet.assets.stock_line_count} lines)`}
                value={sheet.assets.stock_at_cost}
                testid="bs-stock"
              />
              <Row label="Trade receivables (customers owe)" value={sheet.assets.trade_receivables} testid="bs-ar" />
              <Row label="Airtime machine float" value={sheet.assets.airtime_float} testid="bs-airtime" />
              {sheet.assets.other_label && (
                <Row label={sheet.assets.other_label} value={sheet.assets.other_amount} testid="bs-other-asset" />
              )}
            </Section>

            <div className="space-y-4">
              <Section
                title="LIABILITIES"
                colorClass="bg-red-700"
                total={sheet.totals.total_liabilities}
                totalLabel="Total Liabilities"
                totalTestid="bs-total-liab"
              >
                <Row label="Trade payables (owed to suppliers)" value={sheet.liabilities.trade_payables} testid="bs-ap" />
                <Row label="Customer credit balances" value={sheet.liabilities.customer_credits} testid="bs-cust-credits" />
                <Row
                  label="VAT payable to SARS"
                  value={Math.max(sheet.liabilities.vat_payable, 0)}
                  muted={sheet.liabilities.vat_payable <= 0}
                  testid="bs-vat"
                />
                {sheet.liabilities.vat_payable < 0 && (
                  <p className="text-xs text-emerald-600 pl-1">VAT refund due: {fmt(-sheet.liabilities.vat_payable)}</p>
                )}
                <Row label="Loans payable" value={sheet.liabilities.loans_payable} testid="bs-loans" />
                {sheet.liabilities.other_label && (
                  <Row label={sheet.liabilities.other_label} value={sheet.liabilities.other_amount} testid="bs-other-liab" />
                )}
              </Section>

              <Section
                title="EQUITY"
                colorClass="bg-indigo-700"
                total={sheet.totals.total_equity}
                totalLabel="Total Equity"
                totalTestid="bs-total-equity"
              >
                <Row label="Opening equity" value={sheet.equity.opening_equity} testid="bs-opening-equity" />
                <Row label="Retained earnings" value={sheet.equity.retained_earnings} testid="bs-retained" />
              </Section>
            </div>
          </div>

          {/* Balance identity */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">Assets</span>
                <span className="tabular-nums">{fmt(sheet.totals.total_assets)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">Liabilities + Equity</span>
                <span className="tabular-nums">{fmt(sheet.totals.liabilities_plus_equity)}</span>
              </div>
              <div className="border-t mt-2 pt-2 flex items-center justify-between font-bold">
                <span>Difference</span>
                <span
                  className={`tabular-nums ${sheet.balanced ? "text-emerald-700" : "text-red-700"}`}
                  data-testid="bs-difference"
                >
                  {fmt(sheet.totals.total_assets - sheet.totals.liabilities_plus_equity)}
                </span>
              </div>
            </CardContent>
          </Card>

          {sheet.notes && (
            <Card>
              <CardContent className="p-4 text-sm text-slate-700 whitespace-pre-wrap">
                <strong className="text-slate-900">Notes:</strong> {sheet.notes}
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-slate-500 no-print">
            Trade receivables, trade payables, airtime float, and stock-at-cost are pulled LIVE from the same
            canonical formulae the Audit Pack uses — so this sheet will always agree with your Customer
            Master, Supplier list, Stock Master, and Airtime page. Bank, cash, petty cash, loans, and opening
            equity are your own recorded inputs (click <strong>Edit inputs</strong>).
          </p>
        </>
      )}

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Balance Sheet Inputs</DialogTitle>
          </DialogHeader>
          {!settings ? (
            <p className="text-slate-500">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-xs">Bank balance</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.bank_balance ?? 0}
                  onChange={(e) => setSettings({ ...settings, bank_balance: e.target.value })}
                  data-testid="bs-settings-bank"
                />
              </div>
              <div>
                <Label className="text-xs">Cash on hand (till + safe)</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.cash_on_hand ?? 0}
                  onChange={(e) => setSettings({ ...settings, cash_on_hand: e.target.value })}
                  data-testid="bs-settings-cash"
                />
              </div>
              <div>
                <Label className="text-xs">Petty cash float</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.petty_cash_float ?? 0}
                  onChange={(e) => setSettings({ ...settings, petty_cash_float: e.target.value })}
                  data-testid="bs-settings-petty"
                />
              </div>
              <div>
                <Label className="text-xs">Loans payable</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.loans_payable ?? 0}
                  onChange={(e) => setSettings({ ...settings, loans_payable: e.target.value })}
                  data-testid="bs-settings-loans"
                />
              </div>
              <div>
                <Label className="text-xs">Opening equity</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.opening_equity ?? 0}
                  onChange={(e) => setSettings({ ...settings, opening_equity: e.target.value })}
                  data-testid="bs-settings-opening-equity"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Any equity ABOVE this figure counts as retained earnings.
                </p>
              </div>
              <div />
              <div>
                <Label className="text-xs">Other asset label</Label>
                <Input
                  value={settings.other_assets_label ?? ""}
                  onChange={(e) => setSettings({ ...settings, other_assets_label: e.target.value })}
                  placeholder="e.g. Vehicles"
                />
              </div>
              <div>
                <Label className="text-xs">Other asset amount</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.other_assets_amount ?? 0}
                  onChange={(e) => setSettings({ ...settings, other_assets_amount: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Other liability label</Label>
                <Input
                  value={settings.other_liabilities_label ?? ""}
                  onChange={(e) => setSettings({ ...settings, other_liabilities_label: e.target.value })}
                  placeholder="e.g. PAYE payable"
                />
              </div>
              <div>
                <Label className="text-xs">Other liability amount</Label>
                <Input
                  type="number" step="0.01"
                  value={settings.other_liabilities_amount ?? 0}
                  onChange={(e) => setSettings({ ...settings, other_liabilities_amount: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Notes (printed on the sheet)</Label>
                <Textarea
                  rows={3}
                  value={settings.notes ?? ""}
                  onChange={(e) => setSettings({ ...settings, notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={saving || !settings} data-testid="bs-settings-save">
              {saving ? "Saving…" : "Save & recalc"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
